import { useRef, useState } from "react";
import { UploadCloud, Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FilterSelect } from "@/components/common";
import { download } from "@/lib/api";
import { toast } from "sonner";

export function FileDrop({ onFile, busy, testid, template, excelOnly }) {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  const pick = (f) => {
    if (!f) return;
    if (!(excelOnly ? /\.(xlsx|xls)$/i : /\.(xlsx|xls|csv)$/i).test(f.name)) {
      toast.error(excelOnly ? "Please choose an .xlsx or .xls file" : "Please choose an .xlsx, .xls or .csv file");
      return;
    }
    onFile(f);
  };
  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
        onClick={() => ref.current?.click()}
        data-testid={`${testid}-dropzone`}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 sm:p-10 text-center transition-colors duration-150 ${drag ? "border-blue-600 bg-blue-50" : "border-slate-300 bg-white hover:border-blue-400 hover:bg-slate-50"}`}
      >
        {busy ? <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-700" /> : <UploadCloud className="h-8 w-8 mx-auto text-blue-700" />}
        <div className="mt-3 font-medium text-slate-800">{excelOnly ? "Drop your Excel file here, or click to browse" : "Drop your Excel / CSV file here, or click to browse"}</div>
        <div className="text-xs text-slate-500 mt-1">{excelOnly ? "Supports .xlsx and .xls (max 10 MB)" : "Supports .xlsx, .xls and .csv (max 10 MB)"}</div>
        <input
          ref={ref}
          type="file"
          accept={excelOnly ? ".xlsx,.xls" : ".xlsx,.xls,.csv"}
          className="hidden"
          data-testid={`${testid}-file-input`}
          onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }}
        />
      </div>
      {template && (
        <Button variant="outline" size="sm" onClick={() => download(`/templates/${template}`).catch(() => toast.error("Download failed"))} data-testid={`${testid}-template-btn`}>
          <Download className="h-4 w-4" /> Download sample Excel template
        </Button>
      )}
    </div>
  );
}

export function MappingEditor({ headers, mapping, fields, onApply, busy, testid }) {
  const [m, setM] = useState(mapping);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`${testid}-mapping`}>
      <div className="font-heading font-semibold text-slate-800">Column mapping</div>
      <p className="text-xs text-slate-500 mt-1 mb-4">Detected automatically. Change a column if it is wrong, then click Apply.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {fields.map(([f, label, req]) => (
          <div key={f}>
            <div className="text-xs font-medium text-slate-600 mb-1">{label}{req && <span className="text-red-600"> *</span>}</div>
            <FilterSelect
              value={m[f] || ""}
              onChange={(v) => setM({ ...m, [f]: v })}
              options={headers}
              placeholder="— Not mapped —"
              testid={`${testid}-map-${f}`}
              className="w-full"
            />
          </div>
        ))}
      </div>
      <Button className="mt-4 bg-blue-800 hover:bg-blue-900" size="sm" onClick={() => onApply(Object.fromEntries(Object.entries(m).filter(([, v]) => v)))} disabled={busy} data-testid={`${testid}-apply-mapping`}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply mapping
      </Button>
    </div>
  );
}

const STATUS = {
  new: ["New", "bg-blue-50 text-blue-800 border-blue-200"],
  ok: ["Ready", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  possible_duplicate: ["Possible duplicate", "bg-amber-50 text-amber-800 border-amber-200"],
  update: ["Name changed", "bg-amber-50 text-amber-800 border-amber-200"],
  existing: ["Ledger exists", "bg-amber-50 text-amber-800 border-amber-200"],
  unchanged: ["Already exists", "bg-slate-100 text-slate-600 border-slate-200"],
  duplicate: ["Duplicate", "bg-orange-50 text-orange-700 border-orange-200"],
  invalid: ["Invalid", "bg-red-50 text-red-700 border-red-200"],
};

export function StatusPill({ status }) {
  const [l, c] = STATUS[status] || [status, ""];
  return <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${c}`}>{l}</span>;
}

export function SummaryGrid({ items, testid }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid={testid}>
      {items.map(([k, label, v, cls]) => (
        <div key={k} className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
          <div className={`money text-2xl font-bold mt-1 ${cls || "text-slate-900"}`} data-testid={`${testid}-${k}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}
